/**
 * Croatian overrides for the profile screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 *
 * Four families account for nearly every line below:
 *
 *   - *račun* for an account, where Bosnian says *nalog* — in Croatian a
 *     *nalog* is an instruction somebody gives you, not something you sign
 *     into;
 *   - *poveznica* for the thing in the recovery mail, and *ispis* for what
 *     the printer does;
 *   - the uncontracted future — *bit će*, *dobit ćete*, *znat ćete* — which
 *     is orthography rather than word choice, and the one an author copying
 *     a Bosnian sentence across will miss;
 *   - the infinitive where Bosnian uses *da* + present: *prestaje vrijediti*,
 *     *ne može uhvatiti*.
 */

import type { ProfileSlice } from '../bs/profile'

export const profile: Partial<ProfileSlice> = {
  'profile.title': 'Moj račun',
  'profile.subtitle': 'Kontakt podaci, lozinka i zaštita računa.',

  'profile.verified.failed': 'Poveznica za potvrdu nije valjana ili je istekla.',

  'profile.identity.title': 'Podaci o zaposleniku',
  'profile.identity.employeeCode': 'Šifra zaposlenika',
  'profile.identity.note':
    'Ove podatke mijenja administrator. Ako nešto nije točno, javite rukovoditelju.',

  'profile.contact.email.hint':
    'Nije obvezna. Većina radnika je nema — obavijesti u aplikaciji stižu uvijek.',
  'profile.contact.sendLink': 'Pošalji poveznicu',
  'profile.contact.notify': 'Šalji obavijesti i na e-poštu',
  'profile.contact.save': 'Spremi',

  'profile.badge.intro':
    'Umjesto tipkanja šifre i lozinke, skenirajte svoju karticu telefonom i prijava je gotova. Korisno kad stojite pored stroja, s rukavicama.',
  'profile.badge.none': 'Još nemate izdanu karticu.',
  'profile.badge.issued':
    'Kartica vam je već izdana. Kod se ne može ponovno prikazati; ako vam treba nova, stara prestaje vrijediti.',
  'profile.badge.issuedAt':
    'Kartica vam je već izdana — {date}. Kod se ne može ponovno prikazati; ako vam treba nova, stara prestaje vrijediti.',
  'profile.badge.managerWarning':
    'Vaš račun ima proširena prava. Ako izdajete karticu, uključite i dvofaktorsku zaštitu — kartica sama nije dovoljna brana za ovakav račun.',
  'profile.badge.reissueConfirm':
    'Već imate izdanu karticu.\n\nIzdavanjem nove, kartica koju sada nosite prestaje vrijediti odmah. Nastaviti?',
  'profile.badge.revokeConfirm':
    'Poništiti karticu?\n\nKartica odmah prestaje vrijediti. Prijava šifrom i lozinkom i dalje radi.',
  'profile.badge.printHint':
    'Ispišite je sada i plastificirajte. Kod se prikazuje samo ovaj put — ako zatvorite stranicu prije ispisa, izdajte novu karticu.',
  'profile.badge.print': 'Ispiši karticu',

  'profile.password.otherSessionsNote':
    'Nakon promjene, sve druge prijave na vaš račun bit će prekinute — ova ostaje aktivna.',

  'profile.twoFactor.description':
    'Uz lozinku traži i šesteroznamenkasti kod iz aplikacije na telefonu (Google Authenticator, Microsoft Authenticator, Aegis…). Preporučeno za rukovoditelje i administratore.',
  'profile.twoFactor.disableExplain':
    'Račun će ubuduće biti zaštićen samo lozinkom. Potvrdite lozinkom da to zaista želite.',
  'profile.twoFactor.setup.step2.hint': 'Ako kamera ne može uhvatiti kod:',
  'profile.twoFactor.setup.code': 'Šesteroznamenkasti kod',

  'profile.recovery.intro':
    'Spremite ove kodove na sigurno mjesto — svaki vrijedi jednom i služi kako biste ušli u račun ako izgubite telefon.',
  'profile.recovery.note':
    'Kodove možete ponovno pogledati na ovoj stranici dok ste prijavljeni.',

  'profile.sessions.note':
    'Možete biti prijavljeni na više uređaja istodobno. Ako ste izgubili telefon ili ste ostali prijavljeni na zajedničkom računalu, odjavite taj uređaj ovdje.',
  'profile.sessions.loadFailed': 'Popis uređaja nije moguće učitati.',

  'profile.forgot.intro':
    'Upišite svoju šifru zaposlenika. Ako na računu imate potvrđenu e-mail adresu, poslat ćemo vam poveznicu.',
  'profile.forgot.identifier': 'Šifra zaposlenika ili e-mail',
  'profile.forgot.identifier.hint': 'Bilo koje od to dvoje — što god vam je pri ruci.',
  'profile.forgot.submit': 'Pošalji poveznicu',
  'profile.forgot.sendFailed': 'Slanje nije uspjelo. Provjerite vezu i pokušajte ponovno.',
  'profile.forgot.sent.body':
    'Ako račun postoji i ima potvrđenu e-mail adresu, poslali smo poveznicu za postavljanje nove lozinke. Pogledajte i neželjenu poštu.',
  'profile.forgot.sent.expiry':
    'Poveznica vrijedi sat vremena i može se iskoristiti samo jednom.',
  'profile.forgot.sent.noMail':
    'Nema poruke? Ako na vašem računu nema potvrđene e-mail adrese — a većina radnika je nema — obratite se nadređenom. Dobit ćete aktivacijski kod i sami postavljate novu lozinku.',
  'profile.forgot.noEmail.body':
    'Obratite se nadređenom — dobit ćete aktivacijski kod, prijavite se njim i odmah postavljate svoju lozinku.',

  'profile.reset.intro.invitation':
    'Postavite lozinku za račun {code}. Tom šifrom se ubuduće prijavljujete — ne e-mailom.',
  'profile.reset.intro.reset':
    'Postavljate novu lozinku za račun {code}. Znat ćete je samo vi.',
  'profile.reset.submit': 'Spremi novu lozinku',
  'profile.reset.failed': 'Promjena lozinke nije uspjela. Provjerite vezu i pokušajte ponovno.',
  'profile.reset.badLink.title': 'Poveznica nije ispravna',
  'profile.reset.badLink.body':
    'Ova poveznica je nepotpuna — najčešće zato što se prelomila u e-poruci. Zatražite novu i otvorite je jednim klikom iz poruke.',
  'profile.reset.badLink.action': 'Zatraži novu poveznicu',

  'profile.setPassword.intro':
    'Prijavili ste se aktivacijskim kodom koji vam je priopćio nadređeni. Postavite sada lozinku koju znate samo vi — kod nakon toga prestaje vrijediti.',
  'profile.setPassword.submit': 'Spremi lozinku',

  'profile.twoFactor.required':
    'Vaša tvrtka traži dvofaktorsku zaštitu za administratore. Dok je ne uključite, administracija je zaključana.',
}
